import { useFieldArray } from 'react-hook-form'
import { Button } from '@cocoonspace/ui/components/button'
import { SortableList } from '@cocoonspace/ui/components/cocoon/sortable-list'
import { FormControlledInput } from '@cocoonspace/ui/components/form-controlled-input'
import { Input } from '@cocoonspace/ui/components/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@cocoonspace/ui/components/select'
import { useLingui } from '@lingui/react/macro'
import { GripVerticalIcon, PlusIcon, Trash2Icon } from 'lucide-react'

import {
	profileLinkTypes,
	useProfileFormContext,
} from '../use-profile-form.hook'

export const ProfileLinksFields = () => {
	const { t } = useLingui()
	const form = useProfileFormContext()
	const { fields, append, move, remove } = useFieldArray({
		control: form.control,
		name: 'externalLinks',
	})
	const linkTypeLabels: Record<string, string> = {
		website: t`Site web`,
		instagram: 'Instagram',
		linkedin: 'LinkedIn',
		facebook: 'Facebook',
		youtube: 'YouTube',
		tiktok: 'TikTok',
		x: 'X',
	}

	return (
		<div className='flex flex-col gap-3'>
			<span className='text-sm font-medium'>{t`Liens externes`}</span>
			<SortableList
				value={fields}
				getId={(link) => link.id}
				onChange={(next) => {
					const first = fields.findIndex(
						(link, index) => link.id !== next[index]?.id,
					)
					if (first === -1) return
					let last = fields.length - 1
					while (last > first && fields[last].id === next[last]?.id) last--
					if (fields[first].id === next[last]?.id) {
						move(first, last)
					} else {
						move(last, first)
					}
				}}
				renderOverlay={(link) => (
					<div className='bg-background rounded border p-3 shadow-lg'>
						{link.url || linkTypeLabels[link.type] || link.type}
					</div>
				)}
				renderItem={(link, { dragHandleProps }) => {
					const index = fields.findIndex((field) => field.id === link.id)

					return (
						<div className='flex flex-wrap items-start gap-2 sm:flex-nowrap'>
							<button
								type='button'
								className='text-muted-foreground hover:text-foreground mt-2 cursor-grab touch-none'
								{...dragHandleProps.attributes}
								{...dragHandleProps.listeners}
								aria-label={t`Réorganiser le lien`}
							>
								<GripVerticalIcon className='size-5' />
							</button>

							<FormControlledInput
								name={`externalLinks.${index}.type`}
								classNames={{
									formItem: 'w-full pb-0 sm:w-40',
									formMessage: 'relative',
								}}
								render={({ field }) => (
									<Select
										value={field.value}
										onValueChange={field.onChange}
									>
										<SelectTrigger aria-label={t`Type de lien`}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{field.value &&
												!(profileLinkTypes as readonly string[]).includes(
													field.value,
												) && (
													<SelectItem value={field.value}>
														{field.value}
													</SelectItem>
												)}
											{profileLinkTypes.map((type) => (
												<SelectItem key={type} value={type}>
													{linkTypeLabels[type]}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>

							<FormControlledInput
								name={`externalLinks.${index}.url`}
								classNames={{
									formItem: 'min-w-0 flex-1 pb-0',
									formMessage: 'relative',
								}}
								render={({ field }) => (
									<Input
										{...field}
										type='url'
										aria-label={t`Adresse du lien`}
										placeholder='https://'
									/>
								)}
							/>

							<Button
								type='button'
								variant='ghost'
								icon
								aria-label={t`Supprimer le lien`}
								onClick={() => remove(index)}
							>
								<Trash2Icon />
							</Button>
						</div>
					)
				}}
			/>

			<Button
				type='button'
				variant='outline'
				size='sm'
				className='self-start'
				onClick={() => append({ type: 'website', url: '' })}
			>
				<PlusIcon />
				{t`Ajouter un lien`}
			</Button>
		</div>
	)
}
